
// Vue, Decorator, Vuex
import { Component, Watch, Vue, Mixins } from "vue-property-decorator";
import { State, Getter, Action, Mutation, namespace } from "vuex-class";

// Sv components;
import TreeItem from "@/components/TreeView/TreeItem.vue";
import TreeContainer from "@/components/TreeView/TreeContainer.vue";
import ActionList from "@/components/actions/ActionList.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import AddSubAction from "@/components/tasks/AddSubAction.vue";
import EditActionPlan from "@/components/tasks/EditActionPlan.vue";

// Third Party Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vselect3 from "vselect3";

// Mixin/s
import ActionsMixin from "@/mixins/ActionsMixin";
import { validationMixin } from "vuelidate";

// Types
import { GroupWithPermissions } from "@/store/areas/types";
import { TreeView, TreeItemContent, sortedValue } from "@/types/treeView";
import {
	ActionPlan,
	TaskCategory,
	TaskLibrary,
	TaskType,
	ActionPayload,
	ActionPlanCreate,
	ActionTrigger,
	NewActionTrigger,
	ActionPlanCategory
} from "@/store/tasks/types";
import { ActionsList } from "@/types/ActionList";
import { UserGroup } from "@/store/types";

// third party
import { required, requiredIf, minLength, maxLength, numeric } from "vuelidate/lib/validators";
import { filter } from "lodash";

// API
import api from "@/services/api.service";

// Namespaces
const Tasks = namespace("tasks");
const Areas = namespace("areas");

@Component({
	components: {
		"tree-item": TreeItem,
		"tree-container": TreeContainer,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"action-list": ActionList,
		"area-tree-select": AreaTreeSelect,
		vselect3,
		"subaction-modal": AddSubAction,
		"edit-action-plan": EditActionPlan
	},
	mixins: [validationMixin],
	validations: {
		actionPlanTitle: {
			required,
			minLength: minLength(3)
		},
		area: {
			required
		},
		checkedActionList: {
			required
		}
	}
})
export default class ActionPlans extends Mixins(ActionsMixin) {
	// Vuex
	@Tasks.Action fetchTaskTriggers: () => Promise<void>;
	@Tasks.Action fetchActionPlans: () => Promise<void>;
	@Tasks.Action fetchActionLibrary: () => Promise<void>;

	@Tasks.Action addActionToPlan: (payload: ActionPayload) => Promise<void>;
	@Tasks.Action createActionPlan: (payload: ActionPlanCreate) => Promise<ActionPlan>;
	@Tasks.Action deleteActionPlan: (actionPlanId: number) => Promise<void>;
	@Tasks.Action removeActionFromPlan: (payload: ActionPayload) => Promise<void>;
	@Tasks.Action removeChildActionFromPlan: (triggerId: string) => Promise<void>;
	@Tasks.Action updateActionForPlan: (payload: any) => Promise<void>;
	@Tasks.Action updateActionTrigger: (actionTrigger: ActionTrigger) => Promise<void>;
	@Tasks.Action updateActionPlan: (payload: any) => Promise<void>;

	@Tasks.State(state => state.TaskCategories) taskCategories: TaskCategory[];
	@Tasks.State(state => state.TaskLibrary) taskLibrary: TaskLibrary[];
	@Tasks.State(state => state.ActionPlans) actionPlans: ActionPlan[];
	@Tasks.State(state => state.TaskTypes) taskTypes: TaskType[];
	@Tasks.State taskTriggers: ActionTrigger[];

	@Getter("getUserGroups") userGroups: any;
	@Tasks.Getter getActionById: (actionId: number) => TaskLibrary;

	@Areas.State(state => state.AreaList) areaList!: GroupWithPermissions[];

	private selectedActionPlan: ActionPlan = null;
	private selectedAction: TaskLibrary = null;
	private editCondition: boolean = false;
	private deleteActionModal: boolean = false;
	private actionModalVisable: boolean = false;
	private checkedActionList: ActionsList[] = [];
	private actionKeys: number[] = [];
	private expandTree: boolean = false;

	private actionPlanTitle: string = "";

	private conditionText: string = "";

	private area: number = null;

	private actionChildModal: boolean = false;
	private deleteActionPlanModal: boolean = false;

	private actionPlanCategories : ActionPlanCategory;
	private actionPlanCategoryId = ActionPlanCategory.Alarm;

	/**
	 * @summary Mounted Hook
	 * @returns Promise<void>
	 */
	public async mounted(): Promise<void> {
		// Mount method.
		await this.fetchActionPlans();
		await this.fetchTaskTriggers();
	}

	private get reserveTree(): TreeView[] {
		// Flattern tree.
		let flatTree: TreeView[] = [];
		const currentTree: TreeView[] = this.treeViewTasks;

		flatTree = this.reserveTreeItem(currentTree);
		return flatTree;
	}

	private reserveTreeItem(TreeItem, treeLevel: number = 0, disableChildren: boolean = false): TreeView[] {
		let flatTree: TreeView[] = [];
		TreeItem.forEach((treeItem: TreeView) => {
			let item: TreeView = treeItem;
			if (item.children.length > 0) {
				flatTree = flatTree.concat(this.reserveTreeItem(item.children, treeLevel + 1, disableChildren));
			}
			item.additional.root = treeLevel == 0 ? true : false;
			item.children = disableChildren ? [] : item.children;
			flatTree.push(item);
		});
		return flatTree;
	}

	/**
	 * @summary Method that removes actions that are parents
	 */
	private get activeActionLibraries(): TaskLibrary[] {
		let actionLibrary: TaskLibrary[] = this.taskLibrary.filter(
			(taskLibrary: TaskLibrary) => !this.reserveTree.find(treeItem => treeItem.id == taskLibrary.taskID)
		);

		return actionLibrary;
	}

	/**
	 * @summary Computed property to get action plans in desired format.
	 * @returns TreeView array
	 */
	private get treeViewTasks(): TreeView[] {
		if (!this.selectedActionPlan) {
			return [];
		}

		const tasksForSelectedActionPlan: TaskLibrary[] = this.taskLibrary.filter(x =>
			x.scripts.hasOwnProperty(this.selectedActionPlan.scriptID)
		);

		if (!tasksForSelectedActionPlan) {
			return [];
		}

		let treeView: TreeView[] = [];

		tasksForSelectedActionPlan.forEach((task: TaskLibrary) => {
			const treeViewItem = {
				title: this.getTaskTextOrType(task.taskID),
				id: task.taskID,
				order: task.scripts[this.selectedActionPlan.scriptID].order,
				children: this.getActionPlanChild(task.taskID),
				additional: { required: task.scripts ? task.scripts[this.selectedActionPlan.scriptID].required : false }
			}
			treeView.push(treeViewItem);
		});

		// Sort the treeView by the order number
		treeView.sort((a: TreeView, b: TreeView) => {
			return a.order - b.order;
		});

		this.actionKeys = [];
		return treeView;
	}

	/**
	 * @summary Resursive function which setups the childs and the childs children etc. for action plans tree.
	 * @param actionId used to get the children Actions of current action.
	 * @returns List of TreeViews and their children TreeViews
	 */
	private getActionPlanChild(actionId: number): TreeView[] {
		if (!this.taskTriggers || this.taskTriggers.length == 0) return [];

		// This ensures that the incoming action didn't
		// already exist as a parent before this one.
		if (this.actionKeys.find(x => x == actionId)) {
			return [];
		} else {
			this.actionKeys.push(actionId);
		}

		const children: ActionTrigger[] = this.taskTriggers.filter(x => x.taskID == actionId);
		if (!children || children.length == 0) return [];
		let result: TreeView[] = [];
		children.forEach((child: ActionTrigger) => {
			const action: TaskLibrary = this.getActionById(child.triggerTaskID);
			if (!action) return;

			const triggerAction = this.getActionTriggerByActionTriggerId(action.taskID);

			result.push({
				title: this.getTaskTextOrType(action.taskID) ?? "",
				id: action.taskID,
				order: 0,
				children: action ? this.getActionPlanChild(child.triggerTaskID) : [],
				additional: { required: triggerAction ? triggerAction.required : false }
			});
		});

		return result;
	}

	/**
	 * @summary Helper method to set the selected Action by actionId
	 * @param actionId the desired action id to be set as the selected action
	 * @returns void
	 */
	private setselectedAction(actionId: number): void {
		this.selectedAction = this.getActionById(actionId);
	}

	/**
	 * @summary Method to set the selected action and enable edit condition
	 * @param actionid the desired action id to be set as the selected action
	 * @return void
	 */
	private setConditionToEdit(actionId: number): void {
		this.setselectedAction(actionId);
		const condition: ActionTrigger = this.getActionTriggerByActionTriggerId(actionId);
		this.conditionText = condition ? condition.resultCondition : "";
		this.editCondition = true;
	}

	/**
	 * @summary Method to set the selected action and show delete action modal
	 * @param actionId the desired action id to be set as the selected action
	 * @returns void
	 */
	private setActionModal(actionId: number): void {
		this.setselectedAction(actionId);
		this.deleteActionModal = true;
	}

	/**
	 * @summary Method to unset the selected Action and hide delete action modal
	 * @returns void
	 */
	private unselectActionDisableModal(): void {
		this.selectedAction = null;
		this.deleteActionModal = false;
	}

	/**
	 * @summary Method to save the condition for selected Action
	 * @returns void
	 */
	private async saveContition(): Promise<void> {
		let actionTrigger = Object.assign({}, this.getActionTriggerByActionTriggerId(this.selectedAction.taskID));
		if (!actionTrigger) return;
		actionTrigger.resultCondition = this.conditionText;
		await this.updateActionTrigger(actionTrigger);

		this.editCondition = false;
		this.selectedAction = null;
		this.conditionText = "";
	}

	/**
	 * @summary Method to set the selected action and show action child modal
	 * @param actionId the desired action id to be set as the selected action
	 * @returns void
	 */
	private addActionChild(actionId: number): void {
		this.setselectedAction(actionId);
		this.actionChildModal = true;
	}

	private closeSubAction(): void {
		this.actionChildModal = false;
	}

	/**
	 * @summary Method to delete the selected action from selected action plan
	 * @returns Promise<void>
	 */
	private async deleteActionFromActionPlan(): Promise<void> {
		const actionTrigger = this.taskTriggers.find(x => x.triggerTaskID == this.selectedAction.taskID);

		const actionTreeItem: TreeView = this.reserveTree.find(x => x.id == this.selectedAction.taskID);

		if (actionTreeItem && actionTreeItem.additional && !actionTreeItem.additional.root) {
			await this.removeChildActionFromPlan(actionTrigger.triggerID);
		} else {
			const rootPayload: ActionPayload = {
				scriptID: this.selectedActionPlan.scriptID,
				taskID: this.selectedAction.taskID,
				required: true
			};
			// Check if we are removing the root parent or a child.
			await api.triggeredTasksSave(this.selectedAction.taskID, []);
			await this.removeActionFromPlan(rootPayload);
		}

		this.deleteActionModal = false;
		await this.fetchActionPlans();
		await this.fetchActionLibrary();
	}

	/**
	 * @summary Method to create a new action plan
	 * @returns Promise<void>
	 */
	private async saveNewActionPlan(): Promise<void> {
		if (!this.actionPlanTitle || !this.area) return;
		const newActionPlan: ActionPlanCreate = {
			isNew: true,
			edit: true,
			title: this.actionPlanTitle,
			groupID: this.area,
			scriptCategoryID: this.actionPlanCategoryId
		};

		this.selectedActionPlan = await this.createActionPlan(newActionPlan);

		this.area = null;
		this.actionPlanTitle = "";
		this.actionModalVisable = false;

		// Check to see if we have any actions we want to add to the root.
		await this.saveNewRootAction(this.selectedActionPlan.scriptID);
	}

	/**
	 * @summary Method to sort selected actions for saving to action plan root.
	 * @param scriptId action plan to which append the actions too
	 * @returns Promise<void>
	 */
	private async saveNewRootAction(scriptId: number): Promise<void> {
		if (!this.checkedActionList || this.checkedActionList.length == 0) return;

		const actions: ActionPayload[] = this.checkedActionList.map((action: ActionsList) => ({
			required: action.required,
			taskID: action.actionId,
			scriptID: scriptId
		}));
		await this.AddActionsToRoot(actions);
		this.actionModalVisable = false;
	}

	/**
	 * @summary Method to save new actions to action plan root
	 * @param actions Array of Action to add to root.
	 * @returns Promise<void>
	 */
	private async AddActionsToRoot(actions: ActionPayload[]): Promise<void> {
		actions.forEach(async (action: ActionPayload) => {
			const payload: ActionPayload = {
				scriptID: action.scriptID,
				taskID: action.taskID,
				required: action.required
			};
			await this.addActionToPlan(payload);
		});
	}

	/**
	 * @summary Method to hide/show action modal, alsos unsets selected action plan
	 * @returns void
	 */
	public addNewActionPlan(): void {
		this.actionPlanTitle = "";
		this.selectedActionPlan = null;
		this.area = null;
		this.actionModalVisable = !this.actionModalVisable;
	}

	/**
	 * @summary Method to delete the selected action plan, hide action plan modal and unsets selected action plan
	 * @returns Promise<void>
	 */
	private async deleteSelectedActionPlan(): Promise<void> {
		if (!this.selectedActionPlan) return;
		await this.deleteActionPlan(this.selectedActionPlan.scriptID);
		this.deleteActionPlanModal = false;
		this.selectedActionPlan = null;
	}

	/**
	 * @summary handler which sorts the tree with the tree data
	 *
	 */
	private sortActionPlans(sortedItems: sortedValue): void {
		const currentTreeLevelSorted: number[] = sortedItems.tree;
		// This is root level tasks.
		if (sortedItems.level <= 1) {
			currentTreeLevelSorted.forEach(async (actionId: number, index: number, arr: number[]) => {
				const task: TaskLibrary = this.getActionById(actionId);
				const scriptTaskUpdatePayload: ActionPayload = {
					order: index,
					required: task.scripts[this.selectedActionPlan.scriptID].required,
					taskID: actionId,
					scriptID: this.selectedActionPlan.scriptID
				};
				await this.updateActionForPlan(scriptTaskUpdatePayload);
			});
		}
	}

	private async setRequiredState(tree: TreeItemContent): Promise<void> {
		const taskId = tree.item.id;
		const isRequiredCheckboxOnARootAction: boolean = this.treeViewTasks.some((treeItemTask: TreeView) => treeItemTask.id == taskId);
		if (isRequiredCheckboxOnARootAction) {
			const task = this.getActionById(taskId);

			const scriptTaskUpdatePayload: ActionPayload = {
				order: task.scripts[this.selectedActionPlan.scriptID].order ?? 0,
				required: tree.additional.required ?? false,
				taskID: taskId,
				scriptID: this.selectedActionPlan.scriptID
			};
			await this.updateActionForPlan(scriptTaskUpdatePayload);			
		} else {
			let triggerTask = Object.assign({}, this.getActionTriggerByActionTriggerId(taskId));
			if (triggerTask){
				triggerTask.required = !tree.additional.required;
				await this.updateActionTrigger(triggerTask);
			}
		}
	}

	private unEditedActionPlan: ActionPlan;
	private editingActionPlan: boolean = false;

	private onEditActionPlanName() {
		this.onCancelEditingActionPlanName();
		this.unEditedActionPlan = {
			...this.selectedActionPlan
		};

		this.editingActionPlan = true;
	}
	private async onSaveActionPlanName() {
		this.updateActionPlan(this.selectedActionPlan);
		this.editingActionPlan = false;
	}
	private onCancelEditingActionPlanName() {
		if (this.editingActionPlan) {
			Vue.set(this.selectedActionPlan, "title", this.unEditedActionPlan.title);
			Vue.set(this.selectedActionPlan, "groupID", this.unEditedActionPlan.groupID);
			this.editingActionPlan = false;
		}
	}
	private selectExistingActionPlan(action) {
		this.onCancelEditingActionPlanName();
		this.selectedActionPlan = action;
	}

	private get actionPlanCategoryOptions() : any[] {
        let map = [];

        for(var category in ActionPlanCategory) {
            if (typeof ActionPlanCategory[category] === 'number') {
                map.push({label: category, id: parseInt(ActionPlanCategory[category]) });
            }
        }

        return map;
    }
}
